import { Component, inject, signal } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Session } from '../../services/session';
import { AuthService } from '../../services/auth';
import { ApiEndpoints } from '../../core/api-endpoints';
import { LoadingIndicator } from '../../shared/loading-indicator/loading-indicator';
import { IError } from '../../interfaces/error';
import { Router } from '@angular/router';
import { FlashService } from '../../services/flash';

@Component({
  selector: 'app-login',
  imports: [FormsModule, LoadingIndicator],
  templateUrl: './login.html',
  styleUrl: './login.css',
})
export class Login {
  constructor(
    private session: Session,
    private authService: AuthService,
    private router: Router,
  ) {}

  protected username: string = '';
  protected password: string = '';
  protected isLoading = signal<boolean>(false);
  protected readonly flashService = inject(FlashService);

  protected login(form: NgForm): void {
    this.session.clear();

    if (this.isLoading()) {
      return;
    }

    if (form.invalid) {
      this.flashService.failure('Fill all required fields.');
      return;
    }

    if (this.password.length < 8) {
      this.flashService.failure('Password must be at least 8 characters long');
      return;
    }

    const loginForm = {
      username: this.username.toLowerCase(),
      password: this.password,
    };

    this.isLoading.set(true);

    this.authService.login(`${ApiEndpoints.url}/auth/login`, loginForm).subscribe({
      next: (res) => {
        this.session.setItem('token', res.token);
        this.isLoading.set(false);

        if (res.accountStatus === 'PENDING') {
          this.router.navigate(['/set-password']);
          return;
        }

        if (res.role === 'SUPER_ADMIN') {
          this.router.navigate(['/admin-welcome']);
          return;
        }

        this.router.navigate(['/dashboard']);
      },

      error: (err) => {
        this.isLoading.set(false);

        const message = err.error?.message;

        switch (err.status) {
          case 400:
            this.flashService.failure(message ?? 'Fill all required fields.');
            break;

          case 401:
            this.flashService.failure(message ?? 'Invalid username or password');
            break;

          case 403:
            this.flashService.failure(
              message ?? 'Account deactivated. Contact School Administrator.',
            );
            break;

          case 422:
            this.flashService.failure(message ?? 'Invalid password length');
            break;

          default:
            this.flashService.failure(message ?? 'Server Error. Refresh the page.');
        }
      },
    });
  }
}
